import type { UnknownAction } from '@reduxjs/toolkit';
import { Map as ImmutableMap, List, Record, type RecordOf } from 'immutable';

import { accountsTypes } from '../accounts';
import type { Account, AccountSubType, AccountType, UsageType } from '../accounts/accountsTypes';
import type { institutionLoginsTypes } from '../institution-logins';
import type { institutionsTypes } from '../institutions';
import type { createDialog } from '../root-ui/rootUiActions';

export const ACCOUNT_DISCOVERY_REDUCER_KEY = 'upsertInstitutionLoginStore';
export type ACCOUNT_DISCOVERY_REDUCER_KEY = typeof ACCOUNT_DISCOVERY_REDUCER_KEY;

export const DIALOG_TYPE_ACCOUNTS_DISCOVERY = 'DIALOG_TYPE_ACCOUNTS_DISCOVERY';
export type DIALOG_TYPE_ACCOUNTS_DISCOVERY = typeof DIALOG_TYPE_ACCOUNTS_DISCOVERY;

export type AccountDiscoveryMode =
  | 'ADD-FI'
  | 'ADD_FI'
  | 'ADD-FI-ASSETS'
  | 'ADD-FI-MANUAL'
  | 'ADD-PAYMENT-PROVIDER'
  | 'ACCOUNT-REDISCOVERY'
  | 'GSF'
  | 'MIGRATE-INSTITUTION-LOGIN'
  | 'PROVIDE-CREDENTIALS'
  | 'PROVIDE-MFA'
  | 'RE-AUTHENTICATE'
  | 'settings accounts link'
  | 'UPDATE-CREDENTIALS'
  | 'UPDATE-FI';

export type AccountDiscoveryUpdateStrategy = 'REPLACE' | 'UPDATE' | 'UPDATE_ACCOUNTS';

// Single model of the QCS error envelope; sagas, the reducer, and the
// aggErrors map all read from this shape. Every field is optional because the
// value can be a bare JS Error, an HTTP transport error, or a QCS agg error.
export type ResponseError = {
  message?: string;
  status?: string | number;
  statusCode?: string | number;
  code?: string | number;
  aggStatus?: string | number;
  aggStatusCode?: string | number;
  response?: {
    status?: string | number;
    data?: {
      errors?: Array<{
        code?: string | number;
        message?: string;
        extData?: {
          attributeName?: string;
          conflictId?: string;
        };
      }>;
    };
  };
};

export type AggActionDispatchAction = {
  type: string;
  payload?: unknown;
  meta?: unknown;
  error?: boolean;
};

export type AggActionConfirmationAction = ReturnType<typeof createDialog>;

export type AggAction = {
  id?: string | number | null;
  type?: string | null;
  error?: string | null;
  title?: string | null;
  text?: string | null;
  helpText?: string | null;
  status?: string | number | null;
  statusCode?: string | number | null;
  statusText?: string | null;
  code?: string | number | null;
  actionLabel?: string | null;
  action?: AggActionDispatchAction | null;
  actionWithConfirmation?: AggActionConfirmationAction | null;
  showConfirmationDialog?: boolean;
  institutionLogin?: institutionLoginsTypes.InstitutionLogin | null;
  additionalInfo?: string | null;
  migrationEntry?: string;
};

export type AccountDiscoveryDialogProps = {
  mode: AccountDiscoveryMode;
  initiator?: string;
  rootScreen?: string | null;
  additionalTrackingProperties?: { [key: string]: unknown };

  /** Opens the dialog with an institution selected, skipping the picker. */
  institutionId?: string | null;

  institutionLogin?: institutionLoginsTypes.InstitutionLogin | null;
  mfaChallenge?: institutionLoginsTypes.MfaChallenge | RecordOf<institutionLoginsTypes.MfaChallenge>;

  aggAction?: AggAction;
  aggActionShowIntro?: boolean;

  afterCloseAction?: UnknownAction;
  customDialogTitle?: string | null;
  onClose?: (() => void) | null;

  businessId?: string;
};

export const mkAccountDiscoveryDialogProps = Record<AccountDiscoveryDialogProps>({
  mode: 'ADD-FI',
  initiator: undefined,
  rootScreen: undefined,
  additionalTrackingProperties: undefined,

  institutionId: undefined,
  institutionLogin: undefined,
  mfaChallenge: undefined,

  aggAction: undefined,
  aggActionShowIntro: undefined,

  afterCloseAction: undefined,
  customDialogTitle: undefined,
  onClose: undefined,

  businessId: undefined,
});

// =============================================================================
// Account Discovery Data
// =============================================================================

export type DiscoveredAccountProps = {
  shadowAccountId: string | null;

  id: string | null;
  cpId: string | null;
  cpLoginId: string | null;

  channel: string | null;

  type: AccountType | null | undefined;
  subType: AccountSubType | null | undefined;

  name: string | null | undefined;
  number: string | null | undefined;
  maskedNumber: string | null | undefined;

  balance: number | null | undefined;
  balanceAt: string | null | undefined;

  currency: string | null | undefined;

  businessId: string | null | undefined;
  usageType: UsageType | null | undefined;
};

export type DiscoveredAccount = RecordOf<DiscoveredAccountProps>;

export const mkDiscoveredAccount = Record<DiscoveredAccountProps>({
  shadowAccountId: null,

  id: null,
  cpId: null,
  cpLoginId: null,

  channel: null,

  type: null,
  subType: null,

  name: null,
  number: null,
  maskedNumber: null,

  balance: null,
  balanceAt: null,

  currency: null,

  businessId: null,
  usageType: null,
});

export type ActiveDiscoveredAccountProps = {
  account: Account;
  discoveredAccount: DiscoveredAccount;
};

export type ActiveDiscoveredAccount = RecordOf<ActiveDiscoveredAccountProps>;

export const mkActiveDiscoveredAccount = Record<ActiveDiscoveredAccountProps>({
  account: accountsTypes.mkAccount('UNKNOWN'),
  discoveredAccount: mkDiscoveredAccount(),
});

export type AccountDiscoveryDataProps = {
  institutionLoginId: string | null;
  credentialsBlob: string | null;

  status: string | null;

  activeAccounts: List<ActiveDiscoveredAccount> | null;
  deadAccounts: List<Account> | null;
  newAccounts: List<DiscoveredAccount> | null;
  unconnectedAccounts: List<Account> | null;
};

export type AccountDiscoveryData = RecordOf<AccountDiscoveryDataProps>;

export const mkAccountDiscoveryData = Record<AccountDiscoveryDataProps>({
  institutionLoginId: null,
  credentialsBlob: null,

  status: null,

  activeAccounts: null,
  deadAccounts: null,
  newAccounts: null,
  unconnectedAccounts: null,
});

// =============================================================================
// InstitutionLoginUpserter
// =============================================================================

/** A login in the discovery flow; a new one has no id until QCS creates it. */
export type DiscoveryInstitutionLogin = RecordOf<institutionLoginsTypes.InstitutionLoginClient>;

// The two login types differ only in id, so a discovery login with an id, the '0' placeholder included, is an
// InstitutionLogin.
export const getInstitutionLoginWithId = (
  institutionLogin: DiscoveryInstitutionLogin | null | undefined,
): institutionLoginsTypes.InstitutionLogin | undefined =>
  institutionLogin?.id ? (institutionLogin as institutionLoginsTypes.InstitutionLogin) : undefined;

export type InstitutionLoginUpserterDataProps = {
  institution?: institutionsTypes.Institution | RecordOf<institutionsTypes.Institution>;
  channel?: string;

  institutionLogin?: DiscoveryInstitutionLogin | null;
  institutionLoginIdBeingMigrated?: string;

  mfaChallenge?: institutionLoginsTypes.MfaChallenge | RecordOf<institutionLoginsTypes.MfaChallenge> | null;
  partnerAuthUris?: List<string>;
  publicToken?: string;

  accountDiscoveryData?: AccountDiscoveryData | null;
  updateStrategy: AccountDiscoveryUpdateStrategy;

  isAuthenticationDone: boolean;
  isAccountUpsertingDone: boolean;
  isAccountRefreshingDone: boolean;

  isPolling: boolean;
  isSubmitting: boolean;

  error?: AggAction | false | null;
};

export type InstitutionLoginUpserterData = RecordOf<InstitutionLoginUpserterDataProps>;

export const mkInstitutionLoginUpserterData = Record<InstitutionLoginUpserterDataProps>({
  institution: undefined,
  channel: undefined,

  institutionLogin: undefined,
  institutionLoginIdBeingMigrated: undefined,

  mfaChallenge: undefined,
  partnerAuthUris: undefined,
  publicToken: undefined,

  accountDiscoveryData: undefined,
  updateStrategy: 'UPDATE_ACCOUNTS',

  isAuthenticationDone: false,
  isAccountUpsertingDone: false,
  isAccountRefreshingDone: false,

  isPolling: false,
  isSubmitting: false,

  error: undefined,
});

export type InstitutionLoginUpserterStoreProps = {
  upserterData: ImmutableMap<string, InstitutionLoginUpserterData>;
};

export type InstitutionLoginUpserterStore = RecordOf<InstitutionLoginUpserterStoreProps>;

export const mkInstitutionLoginUpserterStore = Record<InstitutionLoginUpserterStoreProps>({
  upserterData: ImmutableMap<string, InstitutionLoginUpserterData>(),
});

// ================================================================================================
// Types AccountDiscovery Status
// ================================================================================================

export type AccountDiscoveryAggregatorStatusProps = {
  isProcessing?: boolean | null;

  aggStatus?: string | null;
  cpAggStatusCode?: string | null;
  cpAggStatusDetail?: string | null;
  userInstructions?: string | null;
};

export type AccountDiscoveryAggregatorStatus = RecordOf<AccountDiscoveryAggregatorStatusProps>;

export const mkAccountDiscoveryAggregatorStatus = Record<AccountDiscoveryAggregatorStatusProps>({
  isProcessing: false,

  aggStatus: 'unknown',

  cpAggStatusCode: undefined,
  cpAggStatusDetail: undefined,
  userInstructions: undefined,
});

export type AccountDiscoveryStatusProps = {
  institutionLoginId: string | null;
  isProcessing: boolean;
  status: string;
  aggregators: List<AccountDiscoveryAggregatorStatus>;
};

export type AccountDiscoveryStatus = RecordOf<AccountDiscoveryStatusProps>;

export const mkAccountDiscoveryStatus = Record<AccountDiscoveryStatusProps>({
  institutionLoginId: null,
  isProcessing: false,
  status: '',
  aggregators: List(),
});

// ================================================================================================
// Account Upserter Types
// ================================================================================================

type AccountDataRecordDefaults = {
  discoveredAccount?: DiscoveredAccount;

  linkToAccountId: string | null | undefined;

  linkableAccountIdsSection1?: List<string | null | undefined>;
  linkableAccountIdsSection2?: List<string | null | undefined>;
  linkableAccountIdsSection3?: List<string | null | undefined>;

  formFieldName?: string;

  type?: ReturnType<typeof accountsTypes.getAccountTypeNodeByQcsType>;
};

export type AccountDataProps = AccountDataRecordDefaults & {
  discoveredAccount: DiscoveredAccount;
  linkableAccountIdsSection1: List<string | null | undefined>;
  linkableAccountIdsSection2: List<string | null | undefined>;
  linkableAccountIdsSection3: List<string | null | undefined>;
  formFieldName: string;
};

export type AccountData = RecordOf<AccountDataRecordDefaults> & AccountDataProps;

interface AccountDataFactory extends Record.Factory<AccountDataRecordDefaults> {
  (values: AccountDataProps): AccountData;
  new (values: AccountDataProps): AccountData;
}

export const mkAccountData = Record<AccountDataRecordDefaults>({
  discoveredAccount: undefined,

  linkToAccountId: undefined,

  linkableAccountIdsSection1: undefined,
  linkableAccountIdsSection2: undefined,
  linkableAccountIdsSection3: undefined,

  formFieldName: undefined,
}) as AccountDataFactory;

// ================================================================================================
// Account Discovery Error Types
// ================================================================================================

export class AccountDiscoveryFailure extends Error {
  aggStatus: string;
  aggStatusCode: string;

  constructor(message: string, aggStatus: string, aggStatusCode: string) {
    super(message);
    this.aggStatus = aggStatus;
    this.aggStatusCode = aggStatusCode;
  }
}

// ================================================================================================
// Aggregation Info Types
// ================================================================================================

export type FinicityAggregationInfoProps = {
  type: 'FINICITY';
  connectUrl?: string;
  pollingRef?: string;
};

export type FinicityAggregationInfo = RecordOf<FinicityAggregationInfoProps>;

export const mkFinicityAggregationInfo = Record<FinicityAggregationInfoProps>({
  type: 'FINICITY',
  connectUrl: undefined,
  pollingRef: undefined,
});

export type PlaidAggregationInfoProps = {
  type: 'PLAID';
  linkToken: string | null;
  pollingRef?: string;
  connectUrl?: string;
};

export type PlaidAggregationInfo = RecordOf<PlaidAggregationInfoProps>;

export const mkPlaidAggregationInfo = Record<PlaidAggregationInfoProps>({
  type: 'PLAID',
  linkToken: null,
  pollingRef: undefined,
  connectUrl: undefined,
});

export type AggregationInfo = FinicityAggregationInfo | PlaidAggregationInfo;

// The saga skips an empty channel and ignores reauthenticate.
export type AggregationInfoRequest = {
  channel?: string | null;
  institution?: institutionsTypes.Institution | null;
  institutionLogin?: DiscoveryInstitutionLogin | null;
  reauthenticate?: boolean;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [ACCOUNT_DISCOVERY_REDUCER_KEY]: InstitutionLoginUpserterStore;
  }
}
